'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { getAccountBlockMessage } from '@/lib/accountStatus';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<any>(null);

  const supabase = createClient();

  const forceLogoutIfBlocked = async (status: string | undefined | null) => {
    const blockMessage = getAccountBlockMessage(status);
    if (!blockMessage) return false;

    await supabase.auth.signOut().catch(() => {});
    setSession(null);
    setUser(null);
    setProfile(null);
    window.location.replace(`/?auth_error=${encodeURIComponent(blockMessage)}`);
    return true;
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      else setProfile(null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const userId = user?.id;
    if (!userId) return;

    const channel = supabase
      .channel(`user-profile-status-${userId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'user_profiles', filter: `id=eq.${userId}` },
        async (payload) => {
          const next = payload.new as { status?: string } | null;
          const nextStatus = next?.status;
          if (nextStatus === undefined) return;
          await forceLogoutIfBlocked(nextStatus);
        }
      )
      .subscribe();

    // Fallback polling in case Realtime is unavailable.
    const intervalId = window.setInterval(() => {
      void fetchProfile(userId);
    }, 15000);

    return () => {
      clearInterval(intervalId);
      void supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error || !data) {
        setProfile(null);
        return;
      }

      setProfile(data);
      await forceLogoutIfBlocked(data.status as string | undefined | null);
    } catch {
      // Profile may not exist yet
      setProfile(null);
    }
  };

  const signUp = async (email: string, password: string, metadata: Record<string, string> = {}) => {
    // Email verification is disabled; users must be created via our signup API
    // which uses the service role + Resend emails.
    const res = await fetch('/api/auth/signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fullName: metadata?.fullName || '',
        email,
        password,
        role: metadata?.role || 'business',
      }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body?.error || 'Signup failed');
    return body;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;

    if (data.user) {
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('status')
        .eq('id', data.user.id)
        .maybeSingle();

      if (profileError || !profile) {
        await supabase.auth.signOut();
        throw new Error('Your account is not active. Please contact support.');
      }

      const blockMessage = getAccountBlockMessage(profile?.status);
      if (blockMessage) {
        await supabase.auth.signOut();
        throw new Error(blockMessage);
      }
    }

    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
  };

  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => user?.email_confirmed_at !== null;

  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    profile,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
